import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiEye, FiTarget, FiStar, FiHexagon, FiTriangle, FiCircle } = FiIcons;

const HolographicVisionChart = ({ data = [], title = "Vision Categories" }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [animatedData, setAnimatedData] = useState([]);

  // DreamForge Vision-themed colors
  const VISION_COLORS = [
    { hex: '#8b5cf6', rgb: '139,92,246', name: 'Vision Purple' },
    { hex: '#ec4899', rgb: '236,72,153', name: 'Dream Pink' },
    { hex: '#06b6d4', rgb: '6,182,212', name: 'Astral Cyan' },
    { hex: '#10b981', rgb: '16,185,129', name: 'Success Green' },
    { hex: '#f59e0b', rgb: '245,158,11', name: 'Ambition Gold' },
    { hex: '#ef4444', rgb: '239,68,68', name: 'Passion Red' },
  ];

  // Process data for animation
  useEffect(() => {
    if (data.length > 0) {
      const initialData = data.map((item, index) => ({
        ...item,
        animatedCount: 0,
        color: VISION_COLORS[index % VISION_COLORS.length],
        glowIntensity: 0,
        angle: (index * 360) / data.length,
      }));

      setAnimatedData(initialData);

      // Stagger animation for mystical effect
      data.forEach((_, index) => {
        setTimeout(() => {
          setAnimatedData(prevData =>
            prevData.map((item, i) =>
              i === index ? {
                ...item,
                animatedCount: item.count,
                glowIntensity: 1
              } : item
            )
          );
        }, index * 400);
      });
    }
  }, [data]);

  // DreamForge Vision tooltip
  const VisionTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="relative bg-gradient-to-br from-purple-900/95 to-pink-900/95 backdrop-blur-xl p-4 rounded-2xl border border-purple-400/50 shadow-dominance"
          style={{
            boxShadow: `
              0 0 30px rgba(139,92,246,0.5),
              inset 0 0 20px rgba(236,72,153,0.1)
            `
          }}
        >
          {/* Vision corner ornaments */}
          <div className="absolute -top-2 -left-2 w-4 h-4">
            <div className="w-full h-full border-t-2 border-l-2 border-purple-400 rounded-tl-lg"></div>
            <div className="absolute top-1 left-1 w-1 h-1 bg-purple-400 rounded-full animate-pulse"></div>
          </div>
          <div className="absolute -top-2 -right-2 w-4 h-4">
            <div className="w-full h-full border-t-2 border-r-2 border-purple-400 rounded-tr-lg"></div>
            <div className="absolute top-1 right-1 w-1 h-1 bg-purple-400 rounded-full animate-pulse"></div>
          </div>
          <div className="absolute -bottom-2 -left-2 w-4 h-4">
            <div className="w-full h-full border-b-2 border-l-2 border-purple-400 rounded-bl-lg"></div>
            <div className="absolute bottom-1 left-1 w-1 h-1 bg-purple-400 rounded-full animate-pulse"></div>
          </div>
          <div className="absolute -bottom-2 -right-2 w-4 h-4">
            <div className="w-full h-full border-b-2 border-r-2 border-purple-400 rounded-br-lg"></div>
            <div className="absolute bottom-1 right-1 w-1 h-1 bg-purple-400 rounded-full animate-pulse"></div>
          </div>

          <div className="flex items-center space-x-3 mb-3">
            <div
              className="w-6 h-6 rounded-full border-2 border-purple-400 flex items-center justify-center"
              style={{ backgroundColor: `${data.color.hex}33` }}
            >
              <div
                className="w-3 h-3 rounded-full animate-pulse"
                style={{ backgroundColor: data.color.hex }}
              ></div>
            </div>
            <p className="font-bold text-purple-200 tracking-wide text-lg">
              {data.category}
            </p>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center text-purple-300">
              <span className="text-purple-400">Total Visions:</span>
              <span className="text-white font-bold text-lg">{data.total}</span>
            </div>
            <div className="flex justify-between items-center text-purple-300">
              <span className="text-purple-400">Achieved:</span>
              <span className="text-green-400 font-bold text-lg">{data.achieved}</span>
            </div>
            <div className="flex justify-between items-center text-purple-300">
              <span className="text-purple-400">Success Rate:</span>
              <span className="text-purple-200 font-bold text-lg">{data.achievementRate}%</span>
            </div>
            <div className="mt-3 pt-2 border-t border-purple-400/30">
              <div className="text-center">
                <span className="text-xs text-purple-400">✦ VISION ENERGY ✦</span>
              </div>
            </div>
          </div>
        </motion.div>
      );
    }
    return null;
  };

  const chartData = animatedData.length > 0 ? animatedData : [];

  return (
    <div className="relative bg-gradient-to-br from-purple-900/20 to-pink-900/20 rounded-3xl border border-purple-400/30 shadow-power overflow-hidden">
      <div className="p-3 sm:p-4 lg:p-6">
        {/* Header */}
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
          <div className="flex items-center space-x-3">
            <motion.div
              className="p-2 sm:p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl shadow-lg"
              animate={{
                boxShadow: [
                  "0 0 20px rgba(139,92,246,0.3)",
                  "0 0 30px rgba(236,72,153,0.5)",
                  "0 0 20px rgba(139,92,246,0.3)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              <SafeIcon icon={FiEye} className="text-white text-lg sm:text-xl" />
            </motion.div>
            <div>
              <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-purple-300 tracking-wide">
                {title}
              </h3>
              <p className="text-purple-400 text-xs sm:text-sm opacity-80">
                ✦ Vision Achievement Analysis ✦
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <motion.div
              animate={{ rotate: 360, scale: [1, 1.1, 1] }}
              transition={{
                rotate: { duration: 20, repeat: Infinity, ease: "linear" },
                scale: { duration: 2, repeat: Infinity }
              }}
              className="w-6 h-6 border-2 border-purple-400/50 rounded-full flex items-center justify-center"
            >
              <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
            </motion.div>
          </div>
        </div>

        {/* Chart container */}
        <div className="relative z-10 h-[300px] sm:h-[350px] lg:h-[400px] mb-4">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <defs>
                {VISION_COLORS.map((colorData, index) => (
                  <React.Fragment key={`gradient-${index}`}>
                    <radialGradient
                      id={`vision-gradient-${index}`}
                      cx="50%"
                      cy="50%"
                      r="80%"
                    >
                      <stop offset="0%" stopColor={colorData.hex} stopOpacity={0.9} />
                      <stop offset="50%" stopColor={colorData.hex} stopOpacity={0.6} />
                      <stop offset="100%" stopColor={colorData.hex} stopOpacity={0.2} />
                    </radialGradient>
                    <filter id={`vision-glow-${index}`} x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </React.Fragment>
                ))}
              </defs>
              <Pie
                data={chartData}
                dataKey="animatedCount"
                nameKey="category"
                cx="50%"
                cy="50%"
                outerRadius={window.innerWidth >= 1024 ? 140 : window.innerWidth >= 640 ? 110 : 90}
                innerRadius={window.innerWidth >= 1024 ? 60 : window.innerWidth >= 640 ? 45 : 35}
                paddingAngle={6}
                animationDuration={3000}
                animationBegin={0}
                onMouseEnter={(_, index) => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={`url(#vision-gradient-${index % VISION_COLORS.length})`}
                    stroke={entry.color.hex}
                    strokeWidth={hoveredIndex === index ? 3 : 1}
                    filter={hoveredIndex === index ? `url(#vision-glow-${index % VISION_COLORS.length})` : 'none'}
                    style={{
                      transform: hoveredIndex === index ? 'scale(1.08)' : 'scale(1)',
                      transformOrigin: 'center',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      filter: hoveredIndex === index ? 'brightness(1.3) saturate(1.2)' : 'brightness(1)',
                    }}
                  />
                ))}
              </Pie>
              <Tooltip content={<VisionTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Vision legend */}
        <div className="relative z-10 space-y-3">
          <div className="text-center">
            <span className="text-xs text-purple-400 tracking-widest">✦ VISION MANIFESTATION GRID ✦</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {chartData.map((category, index) => (
              <motion.div
                key={`legend-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 2 + index * 0.15 }}
                className="flex items-center justify-between bg-gradient-to-r from-purple-900/30 to-pink-900/30 backdrop-blur-xl px-3 py-2 rounded-xl border border-purple-400/20 hover:border-purple-400/50 transition-all duration-300"
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  boxShadow: hoveredIndex === index
                    ? `0 0 20px rgba(${category.color.rgb},0.4), 0 0 30px rgba(139,92,246,0.2)`
                    : `0 0 8px rgba(${category.color.rgb},0.1)`
                }}
              >
                <div className="flex items-center space-x-3">
                  <motion.div
                    className="relative w-4 h-4 rounded-full border border-purple-400"
                    style={{ backgroundColor: category.color.hex }}
                    animate={{
                      boxShadow: [
                        `0 0 5px rgba(${category.color.rgb},0.5)`,
                        `0 0 15px rgba(${category.color.rgb},0.8)`,
                        `0 0 5px rgba(${category.color.rgb},0.5)`
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    <div className="absolute inset-1 rounded-full bg-white/20"></div>
                  </motion.div>
                  <span className="text-sm text-purple-200 font-medium truncate max-w-28 sm:max-w-none">
                    {category.category}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-purple-400">
                    {category.achieved}/{category.total}
                  </span>
                  <span className="text-xs text-purple-300 font-bold">
                    {category.achievementRate}%
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* DreamForge border effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-purple-400 to-transparent opacity-40"></div>
        <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-pink-400 to-transparent opacity-40"></div>
        <div className="absolute top-0 left-0 w-0.5 h-full bg-gradient-to-b from-transparent via-purple-400 to-transparent opacity-40"></div>
        <div className="absolute top-0 right-0 w-0.5 h-full bg-gradient-to-b from-transparent via-pink-400 to-transparent opacity-40"></div>
      </div>
    </div>
  );
};

export default HolographicVisionChart;